import { useEffect, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Handles the redirect-back-from-Stripe flow.
 * If the URL has ?membership_session_id=... we poll the backend until the
 * payment status resolves, then show a toast and clean the URL.
 *
 * If ?membership_cancelled=1 we just show a cancel toast.
 */
export default function PaymentReturnHandler() {
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("membership_session_id");
    const cancelled = params.get("membership_cancelled");

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("membership_session_id");
      url.searchParams.delete("membership_cancelled");
      window.history.replaceState({}, "", url.toString());
    };

    if (cancelled) {
      handled.current = true;
      toast.info("Payment cancelled — no charge was made.");
      cleanUrl();
      return;
    }

    if (!sessionId) return;
    handled.current = true;

    let attempts = 0;
    const maxAttempts = 8;
    const intervalMs = 2000;

    const pollingId = toast.loading("Verifying your payment...");

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(
            `Welcome to ${data.plan_name} — your membership is active.`,
            { id: pollingId }
          );
          cleanUrl();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          toast.error("Payment did not complete. Please try again.", {
            id: pollingId,
          });
          cleanUrl();
          return;
        }
      } catch (err) {
        // continue polling on error
      }
      if (attempts < maxAttempts) {
        setTimeout(poll, intervalMs);
      } else {
        toast.message("Still processing — we'll email you once confirmed.", {
          id: pollingId,
        });
        cleanUrl();
      }
    };

    poll();
  }, []);

  return null;
}
